import * as THREE from "three";
import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

export const useGame = /* @__PURE__ */ create(
  /* @__PURE__ */ subscribeWithSelector<State>((set, get) => {
  return {
    /**
     * Point to move point
     */
    moveToPoint: null as THREE.Vector3 | null,
    isMouseDownForMoveToPoint: false as boolean,

    /**
     * Player position for interaction
     */
    playerPosition: null as THREE.Vector3 | null,
    playerRotation: 0 as number,
    cameraRotation: 0 as number, // New: Camera azimuth for minimap
    nearNPC: false as boolean,

    /**
     * Dynamic NPC positions for minimap
     */
    npcPositions: {} as Record<string, { x: number; y: number; z: number }>,
    setNpcPosition: (id: string, position: { x: number; y: number; z: number }) => {
      set((state) => ({
        npcPositions: {
          ...state.npcPositions,
          [id]: position,
        },
      }));
    },

    /**
     * Camera positions for dialog
     */
    cameraPos: null as { x: number; y: number; z: number } | null,
    cameraTarget: null as { x: number; y: number; z: number } | null,

    /**
     * Character animations state manegement
     */
    // Initial animation
    curAnimation: null as string | null,
    animationSet: {} as AnimationSet,

    initializeAnimationSet: (animationSet: AnimationSet) => {
      set((state) => {
        if (Object.keys(state.animationSet).length === 0) {
          return { animationSet };
        }
        return {};
      });
    },

    reset: () => {
      set((state) => {
        console.log('Resetting animation', state.animationSet.idle);
        return { curAnimation: state.animationSet.idle };
      });
    },

    idle: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.jumpIdle) {
          return { curAnimation: state.animationSet.jumpLand };
        } else if (
          state.curAnimation !== state.animationSet.action1 &&
          state.curAnimation !== state.animationSet.action2 &&
          state.curAnimation !== state.animationSet.action3 &&
          state.curAnimation !== state.animationSet.action4
        ) {
          return { curAnimation: state.animationSet.idle };
        }
        return {};
      });
    },

    walk: () => {
      set((state) => {
        if (state.curAnimation !== state.animationSet.action4) {
          return { curAnimation: state.animationSet.walk };
        }
        return {};
      });
    },

    run: () => {
      set((state) => {
        if (state.curAnimation !== state.animationSet.action4) {
          return { curAnimation: state.animationSet.run };
        }
        return {};
      });
    },

    jump: () => {
      set((state) => {
        return { curAnimation: state.animationSet.jump };
      });
    },

    jumpIdle: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.jump) {
          return { curAnimation: state.animationSet.jumpIdle };
        }
        return {};
      });
    },

    jumpLand: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.jumpIdle) {
          return { curAnimation: state.animationSet.jumpLand };
        }
        return {};
      });
    },

    fall: () => {
      set((state) => {
        return { curAnimation: state.animationSet.fall };
      });
    },

    action1: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.idle) {
          return { curAnimation: state.animationSet.action1 };
        }
        return {};
      });
    },

    action2: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.idle) {
          return { curAnimation: state.animationSet.action2 };
        }
        return {};
      });
    },

    action3: () => {
      set((state) => {
        if (state.curAnimation === state.animationSet.idle) {
          return { curAnimation: state.animationSet.action3 };
        }
        return {};
      });
    },

    action4: () => {
      set((state) => {
        if (
          state.curAnimation === state.animationSet.idle ||
          state.curAnimation === state.animationSet.walk ||
          state.curAnimation === state.animationSet.run
        ) {
          return { curAnimation: state.animationSet.action4 };
        }
        return {};
      });
    },

    /**
     * Additional animations
     */
    // triggerFunction: ()=>{
    //    set((state) => {
    //        return { curAnimation: state.animationSet.additionalAnimation };
    //    });
    // }

    /**
     * Set/get point to move point
     */
    setMoveToPoint: (point: THREE.Vector3 | null) => {
      set(() => {
        return { moveToPoint: point };
      });
    },

    setIsMouseDownForMoveToPoint: (isDown: boolean) => {
      set(() => {
        return { isMouseDownForMoveToPoint: isDown };
      });
    },

    getMoveToPoint: () => {
      return {
        moveToPoint: get().moveToPoint,
      };
    },

    getPlayerPosition: () => {
      return get().playerPosition;
    },

    /**
     * Set player position for interaction
     */
    setPlayerPosition: (position: THREE.Vector3 | null) => {
      set(() => {
        return { playerPosition: position };
      });
    },

    setPlayerRotation: (rotation: number) => {
      set(() => {
        return { playerRotation: rotation };
      });
    },

    setCameraRotation: (rotation: number) => {
      set(() => {
        return { cameraRotation: rotation };
      });
    },

    setNearNPC: (near: boolean) => {
      set(() => {
        return { nearNPC: near };
      });
    },

    getNearNPC: () => {
      return { nearNPC: get().nearNPC };
    },

    /**
     * Set camera positions for dialog
     */
    setCameraPos: (pos: { x: number; y: number; z: number } | null) => {
      set(() => {
        return { cameraPos: pos };
      });
    },

    setCameraTarget: (target: { x: number; y: number; z: number } | null) => {
      set(() => {
        return { cameraTarget: target };
      });
    },

  };
})
);

export type AnimationSet = {
  idle?: string;
  walk?: string;
  run?: string;
  jump?: string;
  jumpIdle?: string;
  jumpLand?: string;
  fall?: string;
  // Currently support four additional animations
  action1?: string;
  action2?: string;
  action3?: string;
  action4?: string;
};

type State = {
  moveToPoint: THREE.Vector3 | null;
  isMouseDownForMoveToPoint: boolean;
  playerPosition: THREE.Vector3 | null;
  playerRotation: number;
  cameraRotation: number;
  nearNPC: boolean;
  npcPositions: Record<string, { x: number; y: number; z: number }>;
  setNpcPosition: (id: string, position: { x: number; y: number; z: number }) => void;
  cameraPos: { x: number; y: number; z: number } | null;
  cameraTarget: { x: number; y: number; z: number } | null;
  curAnimation: string | null;
  animationSet: AnimationSet;
  initializeAnimationSet: (animationSet: AnimationSet) => void;
  reset: () => void;
  setMoveToPoint: (point: THREE.Vector3 | null) => void;
  setIsMouseDownForMoveToPoint: (isDown: boolean) => void;
  getMoveToPoint: () => {
    moveToPoint: THREE.Vector3 | null;
  };
  getPlayerPosition: () => THREE.Vector3 | null;
  setPlayerPosition: (position: THREE.Vector3 | null) => void;
  setPlayerRotation: (rotation: number) => void;
  setCameraRotation: (rotation: number) => void;
  setNearNPC: (near: boolean) => void;
  getNearNPC: () => {
    nearNPC: boolean;
  };
  setCameraPos: (pos: { x: number; y: number; z: number } | null) => void;
  setCameraTarget: (target: { x: number; y: number; z: number } | null) => void;
} & {
  [key in keyof AnimationSet]: () => void;
};
